import { ENDPOINTS } from 'config/endpoints';
import { useQueries, useQuery } from '@tanstack/react-query';
import CronoError from 'crono-fe-common/types/error';
import client from 'utils/clients/client';
import { Request } from 'crono-fe-common/types/request';
import { Response } from 'crono-fe-common/types/response';
import { Account } from 'crono-fe-common/types/account';

const getAccount = (
  id: string | undefined,
  withAnalytics: boolean,
  withScore: boolean,
  withProspectsCount: boolean,
  withOpportunitiesCount: boolean,
) => {
  const params = new URLSearchParams({
    withAnalytics: withAnalytics.toString(),
    withScore: withScore.toString(),
    withProspectsCount: withProspectsCount.toString(),
    withOpportunitiesCount: withOpportunitiesCount.toString(),
  });
  const request: Request = {
    url: `${ENDPOINTS.account.main}/${id}?${params.toString()}`,
    config: {
      method: 'get',
    },
  };
  return client(request) as Promise<Response<Omit<Account, 'prospects'>>>;
};

export default function useGetAccount(
  id?: string,
  withAnalytics = true,
  withScore = false,
  withProspectsCount = false,
  withOpportunitiesCount = false,
  options: {
    onSuccess?: (data: Response<Omit<Account, 'prospects'>>) => void;
    onError?: (error: CronoError) => void;
    onSettled?: () => void;
    /** Keeps showing the previous company's data while a new id loads. */
    keepPreviousData?: boolean;
  } = {},
) {
  return useQuery<Response<Omit<Account, 'prospects'>>, CronoError>({
    queryKey: [
      ENDPOINTS.account.main,
      id,
      withAnalytics,
      withScore,
      withProspectsCount,
      withOpportunitiesCount,
    ],
    queryFn: () =>
      getAccount(
        id,
        withAnalytics,
        withScore,
        withProspectsCount,
        withOpportunitiesCount,
      ),
    enabled: !!id,
    staleTime: 30 * 1000,
    keepPreviousData: options.keepPreviousData,
    onSuccess: options.onSuccess,
    onError: options.onError,
    onSettled: options.onSettled,
  });
}

// Shares useGetAccount's cache (default flags), so a company fetched by either
// is fetched once.
export const useGetAccounts = (ids: string[]) =>
  useQueries({
    queries: ids.map((id) => ({
      queryKey: [ENDPOINTS.account.main, id, true, false, false, false],
      queryFn: () => getAccount(id, true, false, false, false),
      staleTime: 30 * 1000,
    })),
  });
